import MyTests, { upgradeTest } from "@/services/my-tests";

// The Irish panel upgrade is not ready to show customers, but should stay usable in
// development and staging. Gated here rather than in the components because two separate
// surfaces read these getters -- the opt-in panel on Recent Ancestry and the notification
// bell on the dashboard region card -- and hiding only one would leave a badge pointing at
// nothing.
//
// An allowlist rather than a `!== "production"` check so that an unset or unexpected
// environment name hides the feature instead of exposing it. VUE_APP_ENVIRONMENT_NAME is set
// by CI from the job's environment (development/staging/production); NODE_ENV is not usable
// here because staging builds may set it to "production".
const PANEL_UPGRADE_ENVIRONMENTS = ["development", "staging"];
const panelUpgradeVisible = PANEL_UPGRADE_ENVIRONMENTS.includes(
    process.env.VUE_APP_ENVIRONMENT_NAME
);

// Split a dotted numeric version ("2.1.1") into comparable parts. Returns null for anything
// we cannot read as a version, so callers can tell "older" apart from "unknown".
function parseVersion(value) {
    if (typeof value !== "string" && typeof value !== "number") {
        return null;
    }
    const parts = String(value).trim().split(".");
    if (parts.length === 0 || parts[0] === "") {
        return null;
    }
    const numbers = parts.map(part => Number.parseInt(part, 10));
    return numbers.some(Number.isNaN) ? null : numbers;
}

// True only when `candidate` is strictly ahead of `current`. Equal versions are not an
// upgrade, and without two readable versions we cannot show that an upgrade is real --
// offering one that turns out to be identical is exactly the fault this guards against.
function isNewerVersion(candidate, current) {
    const next = parseVersion(candidate);
    const held = parseVersion(current);
    if (next === null || held === null) {
        return false;
    }
    const length = Math.max(next.length, held.length);
    for (let i = 0; i < length; i += 1) {
        const a = next[i] ?? 0;
        const b = held[i] ?? 0;
        if (a !== b) {
            return a > b;
        }
    }
    return false;
}

export default {
    state: {
        kitTests: {}
    },
    getters: {
        kitTests: state => state.kitTests,
        // kitTests is {} until a kit's tests are loaded.
        starterTestMode: state =>
            state.kitTests.autosomal?.purchased === false &&
            state.kitTests.autosomalStarter?.purchased === true,
        autosomalUpdateAvailable: state => {
            // Guard against kitTests not yet loaded (empty {}). Also treat the KitTests
            // default of the string 'false' (and ''/'0'), which is truthy in JS, as
            // not-available so the opt-in only shows for a real update version.
            if (!panelUpgradeVisible) {
                return null;
            }
            const available = state.kitTests?.autosomal?.updateAvailable;
            if (!available || available === "false" || available === "0") {
                return null;
            }
            // The backend can advertise a version the customer already holds. Opting into
            // that reprocesses their DNA, changes nothing, and still sends them a "your
            // results have been updated" email.
            return isNewerVersion(available, state.kitTests?.autosomal?.version)
                ? available
                : null;
        },
        autosomalUpdateInProgress: state =>
            panelUpgradeVisible && state.kitTests?.autosomal?.status === "updating",
    },
    mutations: {
        SET_KIT_TESTS(state, kitTests) {
            state.kitTests = kitTests;
        },
        CLEAR_KIT_TESTS(state) {
            state.kitTests = {};
        },
        upgradeTest(state, test) {
            Object.assign(state.kitTests[test], {
                updateAvailable: false,
                status: "updating",
            });
        },
    },
    actions: {
        setAncestryDone(value){
            this.ancestryDone = value;
        },
        async SET_KIT_TESTS({ commit, getters }, barcode) {
            const tests = await MyTests.getKitTests(barcode, getters.selectedTest.raw_status);
            commit("SET_KIT_TESTS", tests);
        },
        CLEAR_KIT_TESTS({ commit }) {
            commit("CLEAR_KIT_TESTS");
        },
        async upgradeTest({ commit }, { barcode, test }) {
            await upgradeTest(barcode, test);
            commit("upgradeTest", test);
        },
    },
};